import React, { useState, useMemo } from 'react'
import { useEnergyStore } from '@/stores/useEnergyStore'
import { LOCATION_DATA } from '@/data/locationData'
import { Slider, Select, Switch, Collapsible } from '@/components/ui'
import { Save, RotateCcw, ChevronLeft, ChevronRight, Maximize2, Minimize2, Settings2, Pencil, Copy, Check, X, RefreshCw, AlertTriangle } from 'lucide-react'
import { useSimulationData } from '@/hooks/useSimulationData'
import pkg from '../../../package.json'

export const Sidebar: React.FC = () => {
  const config = useEnergyStore((s) => s.config)
  const setConfig = useEnergyStore((s) => s.setConfig)
  const darkMode = useEnergyStore((s) => s.darkMode)
  const sidebarOpen = useEnergyStore((s) => s.sidebarOpen)
  const toggleSidebar = useEnergyStore((s) => s.toggleSidebar)
  const userLevel = useEnergyStore((s) => s.userLevel)
  
  const savedConfigs = useEnergyStore((s) => s.savedConfigs)
  const activeConfigId = useEnergyStore((s) => s.activeConfigId)
  const saveConfigAs = useEnergyStore((s) => s.saveConfigAs)
  const updateSavedConfig = useEnergyStore((s) => s.updateSavedConfig)
  const deleteSavedConfig = useEnergyStore((s) => s.deleteSavedConfig)
  const loadSavedConfig = useEnergyStore((s) => s.loadSavedConfig)

  const [newProfileName, setNewProfileName] = useState('')
  const [isNamingProfile, setIsNamingProfile] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [expandAllCounter, setExpandAllCounter] = useState(0)
  const [collapseAllCounter, setCollapseAllCounter] = useState(0)
  const { status: simStatus } = useSimulationData()

  const renameSavedConfig = useEnergyStore((s) => s.renameSavedConfig)

  const updateConsumptionWeights = (key: string, val: number) => {
    const otherKeys = ['consPeakFrac', 'consShoulderFrac', 'consNightFrac'].filter((k) => k !== key)
    const newVal = val / 100
    const currentOthersSum = otherKeys.reduce(
      (sum, k) => sum + (config[k as keyof typeof config] as number ?? 0),
      0
    )

    const newWeights: any = { [key]: newVal }

    if (currentOthersSum > 0) {
      const ratio = (1 - newVal) / currentOthersSum
      otherKeys.forEach((k) => {
        newWeights[k] = (config[k as keyof typeof config] as number ?? 0) * ratio
      })
    } else {
      const shared = (1 - newVal) / 2
      otherKeys.forEach((k) => {
        newWeights[k] = shared
      })
    }
    setConfig(newWeights)
  }

  const resetConsumptionWeights = () => {
    setConfig({
      consPeakFrac: 0.25,
      consShoulderFrac: 0.1,
      consNightFrac: 0.65,
    })
  }

  const updateExportWeights = (key: string, val: number) => {
    const otherKeys = ['exportPeakFrac', 'exportShoulderFrac', 'exportOffPeakFrac'].filter((k) => k !== key)
    const newVal = val / 100
    const currentOthersSum = otherKeys.reduce(
      (sum, k) => sum + (config[k as keyof typeof config] as number ?? 0),
      0
    )

    const newWeights: any = { [key]: newVal }

    if (currentOthersSum > 0) {
      const ratio = (1 - newVal) / currentOthersSum
      otherKeys.forEach((k) => {
        newWeights[k] = (config[k as keyof typeof config] as number ?? 0) * ratio
      })
    } else {
      const shared = (1 - newVal) / 2
      otherKeys.forEach((k) => {
        newWeights[k] = shared
      })
    }
    setConfig(newWeights)
  }

  const handleSaveProfile = () => {
    if (newProfileName.trim()) {
      saveConfigAs(newProfileName.trim())
      setNewProfileName('')
      setIsNamingProfile(false)
    }
  }

  const locations = Object.keys(LOCATION_DATA).filter(loc => loc !== 'Hamilton')
  
  const hasUnsavedChanges = useMemo(() => {
    if (activeConfigId === 'custom') return false
    const active = savedConfigs.find(c => c.id === activeConfigId)
    if (!active) return false
    return JSON.stringify(active.config) !== JSON.stringify(config)
  }, [activeConfigId, savedConfigs, config])

  return (
    <aside
      className={`fixed top-14 bottom-0 left-0 z-40 transition-all duration-500 ease-in-out border-r ${
        sidebarOpen ? 'w-80 translate-x-0' : 'w-0 -translate-x-full'
      } ${
        darkMode
          ? 'bg-slate-900 border-white/5 shadow-2xl shadow-black/50'
          : 'bg-white border-slate-200 shadow-xl shadow-slate-200/50'
      }`}
    >
      {/* Sidebar Toggle Button (Floating) */}
      <button
        onClick={toggleSidebar}
        className={`fixed top-20 left-0 z-50 p-2 rounded-r-xl border transition-all duration-500 ${
          sidebarOpen ? 'translate-x-80' : 'translate-x-0'
        } ${
          darkMode
            ? 'bg-slate-900 border-white/10 text-slate-400 hover:text-white'
            : 'bg-white border-slate-200 text-slate-500 hover:text-slate-900 shadow-md'
        }`}
      >
        {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
      </button>

      <div className="h-full flex flex-col overflow-hidden">
        <div className={`p-6 border-b ${darkMode ? 'border-white/5' : 'border-slate-100'}`}>
          <div className={`p-4 rounded-2xl border ${darkMode ? 'bg-white/5 border-white/5 shadow-inner' : 'bg-slate-50 border-slate-200'}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className={`text-[10px] font-black uppercase tracking-widest ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Household Profile
              </h2>
              {activeConfigId !== 'custom' && !isRenaming && (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      const entry = savedConfigs.find(c => c.id === activeConfigId)
                      if (entry) {
                        setRenameValue(entry.name)
                        setIsRenaming(true)
                      }
                    }}
                    className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider transition-colors cursor-pointer ${darkMode ? 'text-slate-500 hover:text-emerald-500' : 'text-slate-400 hover:text-emerald-600'}`}
                  >
                    <Pencil size={12} /> Rename
                  </button>
                  <button
                    onClick={() => {
                      const entry = savedConfigs.find(c => c.id === activeConfigId)
                      if (entry) {
                        saveConfigAs(`${entry.name} (Copy)`)
                      }
                    }}
                    className={`flex items-center gap-1.5 text-[9px] font-black uppercase tracking-wider transition-colors ${darkMode ? 'text-slate-500 hover:text-blue-500' : 'text-slate-400 hover:text-blue-600'}`}
                  >
                    <Copy size={12} /> Copy
                  </button>
                </div>
              )}
            </div>

            {isRenaming ? (
              <div className="flex flex-col gap-2 mb-4 animate-in fade-in duration-200">
                <textarea
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      renameSavedConfig(activeConfigId, renameValue)
                      setIsRenaming(false)
                    }
                    if (e.key === 'Escape') setIsRenaming(false)
                  }}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-xl text-xs font-bold outline-none border transition-all resize-none leading-relaxed ${
                    darkMode
                      ? 'bg-white/5 border-emerald-500/30 text-white focus:border-emerald-500 ring-4 ring-emerald-500/10'
                      : 'bg-white border-emerald-500/30 text-slate-900 focus:border-emerald-500 ring-4 ring-emerald-500/10'
                  }`}
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      renameSavedConfig(activeConfigId, renameValue)
                      setIsRenaming(false)
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-[10px] font-black uppercase tracking-wider transition-colors cursor-pointer"
                  >
                    <Check size={14} /> Save Name
                  </button>
                  <button
                    onClick={() => setIsRenaming(false)}
                    className={`px-4 py-2.5 rounded-xl flex items-center justify-center transition-colors cursor-pointer ${
                      darkMode ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900'
                    }`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : isNamingProfile ? (
              <div className="flex flex-col gap-2 mb-4 animate-in fade-in duration-200">
                <textarea
                  value={newProfileName}
                  onChange={(e) => setNewProfileName(e.target.value)}
                  placeholder="Enter profile name..."
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSaveProfile()
                    }
                    if (e.key === 'Escape') setIsNamingProfile(false)
                  }}
                  rows={3}
                  className={`w-full px-4 py-3 rounded-xl text-xs font-bold outline-none border transition-all resize-none leading-relaxed ${
                    darkMode
                      ? 'bg-white/5 border-emerald-500/30 text-white focus:border-emerald-500 ring-4 ring-emerald-500/10'
                      : 'bg-white border-emerald-500/30 text-slate-900 focus:border-emerald-500 ring-4 ring-emerald-500/10'
                  }`}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleSaveProfile}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 text-[10px] font-black uppercase tracking-wider transition-colors cursor-pointer"
                  >
                    <Check size={14} /> Create Profile
                  </button>
                  <button
                    onClick={() => setIsNamingProfile(false)}
                    className={`px-4 py-2.5 rounded-xl flex items-center justify-center transition-colors cursor-pointer ${
                      darkMode ? 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-900'
                    }`}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <div className="mb-4">
                <Select
                  label=""
                  value={activeConfigId}
                  options={[
                    { label: 'Custom Settings', value: 'custom' },
                    ...[...savedConfigs]
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((c) => ({ label: c.name, value: c.id })),
                  ]}
                  onChange={loadSavedConfig}
                  darkMode={darkMode}
                />
              </div>
            )}

            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => {
                  const entry = savedConfigs.find(c => c.id === activeConfigId)
                  setNewProfileName(entry ? `${entry.name} (Copy)` : '')
                  setIsNamingProfile(true)
                }}
                className={`flex items-center justify-center py-2.5 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                  darkMode
                    ? 'bg-white/5 border-white/5 text-slate-400 hover:bg-white/10'
                    : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                Save As New
              </button>
              <button
                onClick={() => activeConfigId !== 'custom' && updateSavedConfig(activeConfigId)}
                disabled={activeConfigId === 'custom'}
                className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                  activeConfigId === 'custom'
                    ? 'opacity-30 cursor-not-allowed'
                    : hasUnsavedChanges
                      ? 'bg-emerald-600 border-emerald-600 text-white hover:bg-emerald-700 shadow-lg shadow-emerald-500/20'
                      : darkMode 
                        ? 'bg-white/5 border-white/5 text-slate-400 hover:bg-white/10'
                        : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {hasUnsavedChanges && <div className="w-1.5 h-1.5 rounded-full bg-white shadow-sm animate-pulse" />}
                Update
              </button>
              <button
                onClick={() => activeConfigId !== 'custom' && deleteSavedConfig(activeConfigId)}
                disabled={activeConfigId === 'custom'}
                className={`flex items-center justify-center py-2.5 rounded-xl text-[9px] font-black uppercase tracking-wider transition-all border cursor-pointer ${
                  activeConfigId === 'custom'
                    ? 'opacity-30 cursor-not-allowed'
                    : 'bg-rose-500/5 border-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white'
                }`}
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Sections Header with Expand/Collapse */}
        <div className="px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Settings2 size={12} className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
            <span className={`text-[9px] font-black uppercase tracking-widest ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Sections
            </span>
          </div>
          <div className="flex gap-4">
            <button 
              onClick={() => setExpandAllCounter(c => c + 1)}
              className={`text-[9px] font-black uppercase tracking-wider transition-colors flex items-center gap-1 ${darkMode ? 'text-emerald-500 hover:text-emerald-400' : 'text-emerald-600 hover:text-emerald-700'}`}
            >
              <Maximize2 size={10} /> Expand
            </button>
            <button 
              onClick={() => setCollapseAllCounter(c => c + 1)}
              className={`text-[9px] font-black uppercase tracking-wider transition-colors flex items-center gap-1 ${darkMode ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <Minimize2 size={10} /> Collapse
            </button>
          </div>
        </div>

        {/* Scrollable Config Sections */}
        <div className="flex-1 overflow-y-auto custom-scrollbar px-6">
          <Collapsible title="General & Simulation" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
            <div className={`p-4 rounded-2xl border space-y-6 ${
              darkMode ? 'bg-slate-500/5 border-slate-500/10' : 'bg-slate-50 border-slate-200'
            }`}>
              <Select
                label="Simulation Location"
                helpKey="Simulation Location"
                value={config.location}
                options={locations}
                onChange={(v) => setConfig({ location: v as any })}
                darkMode={darkMode}
              />
              {config.location === 'Custom' && (
                <Slider
                  label="Solar Generation Multiplier"
                  helpKey="Simulation Location"
                  value={Math.round((config.customLocationMultiplier ?? 1.0) * 100)}
                  unit="%"
                  min={50}
                  max={150}
                  step={1}
                  onChange={(v) => setConfig({ customLocationMultiplier: v / 100 })}
                  darkMode={darkMode}
                />
              )}
              {userLevel !== 'basic' && (
                <div className="space-y-2">
                  <Switch
                    label="Hourly Simulation"
                    helpKey="Hourly Simulation"
                    description="Historic solar irradiance API"
                    enabled={config.hourlySimulation}
                    onChange={(v) => setConfig({ hourlySimulation: v })}
                    darkMode={darkMode}
                  />
                  {config.hourlySimulation && (
                    <div className="pl-1 animate-in fade-in slide-in-from-top-1 duration-300">
                      {simStatus === 'loading' && (
                        <p className={`text-[9px] font-medium flex items-center gap-1.5 animate-pulse ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                          <RefreshCw size={10} className="animate-spin" /> Downloading satellite data...
                        </p>
                      )}
                      {simStatus === 'success' && (
                        <p className={`text-[9px] font-medium flex items-center gap-1.5 ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          <Check size={10} /> Historic curve applied
                        </p>
                      )}
                      {simStatus === 'fallback' && (
                        <p className={`text-[9px] font-medium flex items-start gap-1.5 leading-tight ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          <AlertTriangle size={10} className="shrink-0 mt-[1px]" />
                          <span className="opacity-90">API offline. Using synthetic NZ solar curve.</span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Collapsible>

          <Collapsible title="Consumption Profile" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
            <div className="space-y-4">
              <div className={`p-4 rounded-2xl border space-y-6 ${
                darkMode ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50/50 border-emerald-100'
              }`}>
                <Select
                  label="Input Mode"
                  helpKey="Input Mode"
                  value={config.consMode}
                  options={[
                    { label: 'Monthly Average', value: 'monthly' },
                    { label: 'Daily Average', value: 'daily' },
                    { label: 'Weekly Average', value: 'weekly' },
                    { label: 'Annual Total', value: 'annual' },
                  ]}
                  onChange={(v) => setConfig({ consMode: v as any })}
                  darkMode={darkMode}
                />
                <Slider
                  label="Base Consumption"
                  helpKey="Base Consumption"
                  value={config.consumption}
                  unit="kWh"
                  min={0}
                  max={5000}
                  step={10}
                  onChange={(v) => setConfig({ consumption: v })}
                  darkMode={darkMode}
                />
              </div>
              {userLevel === 'advanced' && (
                <div className={`p-4 rounded-2xl border space-y-4 ${
                  darkMode ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-100'
                }`}>
                  <p className={`text-[10px] font-black uppercase tracking-wider text-emerald-500/80`}>Load Distribution %</p>
                  <Slider
                    label="Peak (7-9am, 5-9pm)"
                    helpKey="Peak (7-9am, 5-9pm)"
                    value={Math.round(config.consPeakFrac * 100)}
                    unit="%"
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) => updateConsumptionWeights('consPeakFrac', v)}
                    darkMode={darkMode}
                  />
                  <Slider
                    label="Shoulder (9am-5pm)"
                    helpKey="Shoulder (9am-5pm)"
                    value={Math.round(config.consShoulderFrac * 100)}
                    unit="%"
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) => updateConsumptionWeights('consShoulderFrac', v)}
                    darkMode={darkMode}
                  />
                  <Slider
                    label="Night (9pm-7am)"
                    helpKey="Night (9pm-7am)"
                    value={Math.round(config.consNightFrac * 100)}
                    unit="%"
                    min={0}
                    max={100}
                    step={1}
                    onChange={(v) => updateConsumptionWeights('consNightFrac', v)}
                    darkMode={darkMode}
                  />
                  <div className="flex items-center justify-between pt-2 border-t border-emerald-500/10">
                    <span className={`text-[9px] font-black uppercase tracking-wider ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      Total: {Math.round((config.consPeakFrac + config.consShoulderFrac + config.consNightFrac) * 100)}%
                    </span>
                    <button
                      onClick={resetConsumptionWeights}
                      className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-wider transition-colors cursor-pointer ${darkMode ? 'text-emerald-500 hover:text-emerald-400' : 'text-emerald-600 hover:text-emerald-700'}`}
                    >
                      <RotateCcw size={10} /> Reset
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Collapsible>

          <Collapsible title="Solar Generation" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
            <div className={`p-4 rounded-2xl border space-y-6 ${
              darkMode ? 'bg-amber-500/5 border-amber-500/10' : 'bg-amber-50/50 border-amber-100'
            }`}>
              <Switch
                label="Add Solar to Profile"
                helpKey="Add Solar to Profile"
                enabled={config.useSolar !== false}
                onChange={(v) => setConfig({ useSolar: v })}
                darkMode={darkMode}
              />
              {config.useSolar !== false && (
                <>
                  <Slider
                    label="PV System Size"
                    helpKey="PV System Size"
                    value={config.pvSize}
                    unit="kWp"
                    min={0}
                    max={50}
                    step={0.1}
                    onChange={(v) => setConfig({ pvSize: v })}
                    darkMode={darkMode}
                  />
                  {userLevel === 'advanced' && (
                    <Slider
                      label="Generation Factor"
                      helpKey="Generation Factor"
                      value={config.pvGeneration}
                      unit="%"
                      min={50}
                      max={150}
                      step={1}
                      onChange={(v) => setConfig({ pvGeneration: v })}
                      darkMode={darkMode}
                    />
                  )}
                  {userLevel !== 'basic' && (
                    <Slider
                      label="System Losses"
                      helpKey="System Losses"
                      value={config.systemLosses}
                      unit="%"
                      min={0}
                      max={40}
                      step={0.5}
                      onChange={(v) => setConfig({ systemLosses: v })}
                      darkMode={darkMode}
                    />
                  )}
                </>
              )}
            </div>
          </Collapsible>

          {userLevel === 'advanced' && (
            <Collapsible title="Grid & Export Settings" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
              <div className="space-y-4">
                <div className={`p-4 rounded-2xl border space-y-6 ${
                  darkMode ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50/50 border-blue-100'
                }`}>
                  {config.useSolar !== false && (
                    <>
                      <Slider
                        label="Export Limit"
                        helpKey="Export Limit"
                        value={config.exportLimit}
                        unit="kW"
                        min={0}
                        max={15}
                        step={0.5}
                        onChange={(v) => setConfig({ exportLimit: v })}
                        darkMode={darkMode}
                      />
                      <Slider
                        label="Export Curtailment"
                        helpKey="Export Curtailment"
                        value={config.exportCurtailment}
                        unit="%"
                        min={0}
                        max={100}
                        step={1}
                        onChange={(v) => setConfig({ exportCurtailment: v })}
                        darkMode={darkMode}
                      />
                    </>
                  )}
                  <Slider
                    label="Line Loss Factor"
                    helpKey="Line Loss Factor"
                    value={config.lineLossFactor}
                    unit="%"
                    min={0}
                    max={20}
                    step={0.1}
                    onChange={(v) => setConfig({ lineLossFactor: v })}
                    darkMode={darkMode}
                  />
                </div>
                {config.useSolar !== false && (
                  <>
                    <div className={`p-4 rounded-2xl border space-y-4 ${
                      darkMode ? 'bg-blue-500/5 border-blue-500/10' : 'bg-blue-50 border-blue-100'
                    }`}>
                      <p className={`text-[10px] font-black uppercase tracking-wider text-blue-500/80`}>Solar Self-Use Weights</p>
                      <Slider
                        label="Solar Peak %"
                        helpKey="Solar Self-Use Weights"
                        value={Math.round((config.solarPeakFrac ?? 0.2) * 100)}
                        unit="%"
                        min={0}
                        max={100}
                        step={1}
                        onChange={(v) => setConfig({ solarPeakFrac: v / 100, solarShoulderFrac: 1 - (v / 100) })}
                        darkMode={darkMode}
                      />
                      <Slider
                        label="Solar Shoulder %"
                        helpKey="Solar Self-Use Weights"
                        value={Math.round((config.solarShoulderFrac ?? 0.8) * 100)}
                        unit="%"
                        min={0}
                        max={100}
                        step={1}
                        onChange={(v) => setConfig({ solarShoulderFrac: v / 100, solarPeakFrac: 1 - (v / 100) })}
                        darkMode={darkMode}
                      />
                    </div>
                    <div className={`p-4 rounded-2xl border space-y-4 ${
                      darkMode ? 'bg-amber-500/5 border-amber-500/10' : 'bg-amber-50 border-amber-100'
                    }`}>
                      <p className={`text-[10px] font-black uppercase tracking-wider text-amber-500/80`}>Export FiT Weights</p>
                      <Slider
                        label="Export Peak %"
                        helpKey="Export FiT Weights"
                        value={Math.round((config.exportPeakFrac ?? 0.15) * 100)}
                        unit="%"
                        min={0}
                        max={100}
                        step={1}
                        onChange={(v) => updateExportWeights('exportPeakFrac', v)}
                        darkMode={darkMode}
                      />
                      <Slider
                        label="Export Shoulder %"
                        helpKey="Export FiT Weights"
                        value={Math.round((config.exportShoulderFrac ?? 0.0) * 100)}
                        unit="%"
                        min={0}
                        max={100}
                        step={1}
                        onChange={(v) => updateExportWeights('exportShoulderFrac', v)}
                        darkMode={darkMode}
                      />
                      <Slider
                        label="Export Off-Peak %"
                        helpKey="Export FiT Weights"
                        value={Math.round((config.exportOffPeakFrac ?? 0.85) * 100)}
                        unit="%"
                        min={0}
                        max={100}
                        step={1}
                        onChange={(v) => updateExportWeights('exportOffPeakFrac', v)}
                        darkMode={darkMode}
                      />
                    </div>
                  </>
                )}
              </div>
            </Collapsible>
          )}

          {config.useSolar !== false && (
            <Collapsible title="Battery Storage" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
              <div className={`p-4 rounded-2xl border space-y-6 ${
                darkMode ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50/50 border-emerald-100'
              }`}>
                <Switch
                  label="Enable Battery (ESS)"
                  helpKey="Enable Battery (ESS)"
                  enabled={config.useEss}
                  onChange={(v) => setConfig({ useEss: v })}
                  darkMode={darkMode}
                />
                <Slider
                  label="Usable Capacity"
                  helpKey="Usable Capacity"
                  value={config.batteryCapacity}
                  unit="kWh"
                  min={0}
                  max={100}
                  step={0.5}
                  onChange={(v) => setConfig({ batteryCapacity: v })}
                  darkMode={darkMode}
                />
              </div>
            </Collapsible>
          )}

          <Collapsible title="Electric Vehicle" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
            <div className={`p-4 rounded-2xl border space-y-6 ${
              darkMode ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50/50 border-indigo-100'
            }`}>
              <Switch
                label="Add EV to Load"
                helpKey="Add EV to Load"
                enabled={config.useEv}
                onChange={(v) => setConfig({ useEv: v })}
                darkMode={darkMode}
              />
              <div className="space-y-6">
                {userLevel !== 'basic' && (
                  <Select
                    label="Dist. Input Mode"
                    helpKey="Dist. Input Mode"
                    value={config.evDistMode}
                    options={[
                      { label: 'Daily km', value: 'daily' },
                      { label: 'Weekly km', value: 'weekly' },
                      { label: 'Monthly km', value: 'monthly' },
                    ]}
                    onChange={(v) => setConfig({ evDistMode: v as any })}
                    darkMode={darkMode}
                  />
                )}
                <Slider
                  label="Commute Distance"
                  helpKey="Commute Distance"
                  value={config.evDistance}
                  unit="km"
                  min={0}
                  max={5000}
                  step={1}
                  onChange={(v) => setConfig({ evDistance: v })}
                  darkMode={darkMode}
                />
                {userLevel !== 'basic' && (
                  <Slider
                    label="EV Efficiency"
                    helpKey="EV Efficiency"
                    value={config.evEfficiency}
                    unit="kWh/100"
                    min={10}
                    max={30}
                    step={0.5}
                    onChange={(v) => setConfig({ evEfficiency: v })}
                    darkMode={darkMode}
                  />
                )}
                {userLevel !== 'basic' && (
                  <Select
                    label="Charging Profile"
                    helpKey="Charging Profile"
                    value={config.evChargeProfile}
                    options={[
                      { label: 'Night (Off-Peak)', value: 'night' },
                      { label: 'Shoulder (Free Window)', value: 'shoulder' },
                      { label: 'Day (Peak)', value: 'peak' },
                    ]}
                    onChange={(v) => setConfig({ evChargeProfile: v as any })}
                    darkMode={darkMode}
                  />
                )}
                {userLevel !== 'basic' && (
                  <Slider
                    label="Battery Capacity"
                    helpKey="EV Battery Capacity"
                    value={config.evBatteryCapacity ?? 60}
                    unit="kWh"
                    min={10}
                    max={120}
                    step={1}
                    onChange={(v) => setConfig({ evBatteryCapacity: v })}
                    darkMode={darkMode}
                  />
                )}
                {userLevel !== 'basic' && config.evChargeProfile === 'shoulder' && (() => {
                  const weeklyKm = config.evDistMode === 'daily' ? config.evDistance * 7
                    : config.evDistMode === 'weekly' ? config.evDistance
                    : config.evDistance / 4.345
                  const weeklyKwh = (weeklyKm / 100) * config.evEfficiency
                  const evBatCap = config.evBatteryCapacity ?? 60
                  const fits = weeklyKwh <= evBatCap
                  return (
                    <div className={`p-2.5 rounded-xl border text-[9px] font-black uppercase tracking-tight leading-relaxed ${
                      fits
                        ? darkMode ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-100 text-emerald-700'
                        : darkMode ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-amber-50 border-amber-100 text-amber-700'
                    }`}>
                      {fits ? '✓' : '⚠'} {weeklyKwh.toFixed(1)} kWh/week needed
                      {fits
                        ? ` — fits in ${evBatCap} kWh battery`
                        : ` — exceeds ${evBatCap} kWh battery, mid-week top-up needed`
                      }
                    </div>
                  )
                })()}
              </div>
            </div>
          </Collapsible>

          {userLevel !== 'basic' && (
            <Collapsible title="Financials & ROI" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
              <div className={`p-4 rounded-2xl border space-y-6 ${
                darkMode ? 'bg-slate-500/5 border-slate-500/10' : 'bg-slate-50 border-slate-200'
              }`}>
                <Switch
                  label="Show ROI Calculator"
                  helpKey="Show ROI Calculator"
                  enabled={config.showRoi}
                  onChange={(v) => setConfig({ showRoi: v })}
                  darkMode={darkMode}
                />
                {config.useSolar !== false && (
                  <Slider
                    label="Total System Cost"
                    helpKey="Total System Cost"
                    value={config.systemCost}
                    unit="$"
                    min={0}
                    max={100000}
                    step={500}
                    onChange={(v) => setConfig({ systemCost: v })}
                    darkMode={darkMode}
                  />
                )}
                {userLevel === 'advanced' && (
                  <>
                    <Slider
                      label="Est. Annual Price Hike"
                      helpKey="Est. Annual Price Hike"
                      value={config.priceHike}
                      unit="%"
                      min={0}
                      max={20}
                      step={0.5}
                      onChange={(v) => setConfig({ priceHike: v })}
                      darkMode={darkMode}
                    />
                    <Switch
                      label="Use Compounding"
                      helpKey="Use Compounding"
                      description="For ROI calculations"
                      enabled={config.useCompounding}
                      onChange={(v) => setConfig({ useCompounding: v })}
                      darkMode={darkMode}
                    />
                  </>
                )}
              </div>
            </Collapsible>
          )}

          {userLevel !== 'basic' && (
            <Collapsible title="Electric Vehicle ROI" darkMode={darkMode} expandTrigger={expandAllCounter} collapseTrigger={collapseAllCounter}>
              <div className={`p-4 rounded-2xl border space-y-6 ${
                darkMode ? 'bg-indigo-500/5 border-indigo-500/10' : 'bg-indigo-50 border-indigo-100'
              }`}>
                <Switch
                  label="Include EV in ROI"
                  helpKey="Include EV in ROI"
                  description="Factor EV cost/petrol savings in payback"
                  enabled={config.includeEvInRoi}
                  onChange={(v) => setConfig({ includeEvInRoi: v })}
                  darkMode={darkMode}
                />
                {config.includeEvInRoi && (
                  <>
                    <Slider
                      label="EV Upfront Cost"
                      helpKey="EV Upfront Cost"
                      value={config.evUpfrontCost ?? 45000}
                      unit="$"
                      min={0}
                      max={150000}
                      step={1000}
                      onChange={(v) => setConfig({ evUpfrontCost: v })}
                      darkMode={darkMode}
                    />
                    {userLevel === 'advanced' && (
                      <>
                        <Slider
                          label="Petrol Car Consumption"
                          helpKey="Petrol Car Consumption"
                          value={config.petrolEfficiency ?? 8.0}
                          unit="L/100km"
                          min={3}
                          max={20}
                          step={0.1}
                          onChange={(v) => setConfig({ petrolEfficiency: v })}
                          darkMode={darkMode}
                        />
                        <Slider
                          label="Petrol Price"
                          helpKey="Petrol Price"
                          value={config.petrolPrice ?? 2.80}
                          unit="$/L"
                          min={1.50}
                          max={4.50}
                          step={0.05}
                          onChange={(v) => setConfig({ petrolPrice: v })}
                          darkMode={darkMode}
                        />
                        <Slider
                          label="EV Road User Charges"
                          helpKey="EV Road User Charges"
                          value={config.evRucRate ?? 76.0}
                          unit="$/1000km"
                          min={0}
                          max={150}
                          step={1}
                          onChange={(v) => setConfig({ evRucRate: v })}
                          darkMode={darkMode}
                        />
                        <Slider
                          label="EV Tyre Wear"
                          helpKey="EV Tyre Wear"
                          value={config.evTireWear ?? 200}
                          unit="$/yr"
                          min={0}
                          max={2000}
                          step={50}
                          onChange={(v) => setConfig({ evTireWear: v })}
                          darkMode={darkMode}
                        />
                        <Slider
                          label="EV Insurance Premium"
                          helpKey="EV Insurance Premium"
                          value={config.evInsurancePremium ?? 300}
                          unit="$/yr"
                          min={0}
                          max={3000}
                          step={50}
                          onChange={(v) => setConfig({ evInsurancePremium: v })}
                          darkMode={darkMode}
                        />
                        <Slider
                          label="EV Servicing"
                          helpKey="EV Servicing"
                          value={config.evServicing ?? 150}
                          unit="$/yr"
                          min={0}
                          max={1000}
                          step={10}
                          onChange={(v) => setConfig({ evServicing: v })}
                          darkMode={darkMode}
                        />
                        <Slider
                          label="EV Steep Depreciation"
                          helpKey="EV Steep Depreciation"
                          value={config.evDepreciation ?? 2500}
                          unit="$/yr"
                          min={0}
                          max={10000}
                          step={100}
                          onChange={(v) => setConfig({ evDepreciation: v })}
                          darkMode={darkMode}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
            </Collapsible>
          )}
        </div>

        {/* Sidebar Footer */}
        <div className={`p-6 border-t ${darkMode ? 'border-white/5' : 'border-slate-100'}`}>
          <p className={`text-[10px] text-center font-bold tracking-widest uppercase opacity-30 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
            Engine v{pkg.version}
          </p>
        </div>
      </div>
    </aside>
  )
}
